import { AppNotification, NotificationKind } from '../data/notifications';
import { api } from './client';

/**
 * The bell, from the platform.
 *
 * `GET /notifications` lists the pushes sent to whoever is signed in - newest
 * first, last 30 days, one row per event - and it serves every actor from the
 * token, so there is nothing to pass but the page size. The Pro app has read
 * it for its bell all along; the customer app kept an empty array with a note
 * saying no such endpoint existed, so every push a customer received vanished
 * from the app the moment it left the notification shade.
 */
type InboxRow = {
  id: string;
  title: string | null;
  body: string | null;
  templateKey: string;
  bookingId: string | null;
  createdAt: string;
  readAt: string | null;
};

type InboxPage = { data: InboxRow[] } | InboxRow[];

/**
 * Which chip a notification files under, from the event that sent it.
 *
 * The template key's prefix is the event family (`booking.cancelled`,
 * `wallet.voucher_coins_unlocked`, `admin.campaign.<id>`), so the mapping is
 * by family and an event nobody listed here still lands under "Updates"
 * rather than disappearing.
 */
const kindOf = (templateKey: string): NotificationKind => {
  const family = templateKey.split('.')[0];
  if (family === 'booking' || family === 'dispatch' || family === 'ride') {
    return 'booking';
  }
  if (family === 'wallet' || family === 'referral' || family === 'admin') {
    return 'offer';
  }
  if (family === 'support' || family === 'safety') {
    return 'reminder';
  }
  return 'account';
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/**
 * "Just now", "12 min ago", "3 h ago", "Yesterday", or the date.
 *
 * Worded for `NotificationsScreen.bucketOf`, which groups by reading this
 * string: "now" and "ago" file under Today, "Yesterday" under Yesterday, and
 * a date under Earlier.
 */
export const whenLabel = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  const gap = now.getTime() - at.getTime();
  if (gap < MINUTE) return 'Just now';
  const sameDay = at.toDateString() === now.toDateString();
  if (sameDay && gap < HOUR) return `${Math.floor(gap / MINUTE)} min ago`;
  if (sameDay) return `${Math.floor(gap / HOUR)} h ago`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (at.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return at.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export const toAppNotification = (
  row: InboxRow,
  now: Date = new Date(),
): AppNotification => ({
  id: row.id,
  kind: kindOf(row.templateKey),
  title: row.title ?? 'Homingo',
  body: row.body?.trim() ?? '',
  when: whenLabel(row.createdAt, now),
  read: row.readAt !== null,
  bookingId: row.bookingId ?? undefined,
});

export const fetchNotifications = async (
  signal?: AbortSignal,
): Promise<AppNotification[]> => {
  const page = await api.get<InboxPage>('/notifications', {
    signal,
    query: { limit: 50 },
  });
  const rows = Array.isArray(page) ? page : page?.data ?? [];
  const now = new Date();
  return rows.map(row => toAppNotification(row, now));
};

export const markNotificationRead = (id: string) =>
  api.post<void>(`/notifications/${id}/read`, {});

export const markAllNotificationsRead = () =>
  api.post<void>('/notifications/read-all', {});
